// src/AdminPanel.js - Tabbed Interface Version
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import './AdminPanel.css';
import DavActivity from './DavActivity';
import TimelineBackup from './TimelineBackup';
import FileManager from './FileManager';
import { getRuntimeConfig } from '../config';
import { SCOPES, emptyAccess, scopesByOrder, capsByOrder } from '../permissions';

// Helper functions for user status
const formatLastActivity = (dateString) => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const formattedTime = date.toLocaleString('en-GB', {
      timeZone: 'Africa/Cairo',
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true 
    });
    
    const parts = formattedTime.split(', ');
    return `${parts[0]} @ ${parts[1].replace(':', '.').replace(/ /g, '').toLowerCase()}`;
  } catch (error) {
    return 'Invalid date';
  }
};

const getUserStatusType = (user) => {
  // Check if truly online (has recent activity)
  const isOnline = user.is_online === true || user.is_currently_online === true;
  
  // Check if never logged in
  const neverLogged = !user.last_login || 
                      user.last_login === 'Never' || 
                      user.last_login === null;
  
  if (isOnline) return 'online';
  if (neverLogged) return 'never-logged';
  return 'offline-recent';
};

const formatActivityText = (user, statusType) => {
  if (statusType === 'online') return '🟢 Online now';
  if (statusType === 'never-logged') return '❌ Never logged in';
  if (user.last_logout) return `Last logout: ${formatLastActivity(user.last_logout)}`;
  return `Last login: ${formatLastActivity(user.last_login)}`;
};

const AdminPanel = forwardRef((props, ref) => {
  // Active tab state
  const [activeTab, setActiveTab] = useState('users');
  const [storageInitialTab, setStorageInitialTab] = useState('live');

  // Users tab states
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for backup/restore operations
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [srvBackupInProgress, setSrvBackupInProgress] = useState(false);
  const [srvRestoreInProgress, setSrvRestoreInProgress] = useState(false);
  const [srvBackupList, setSrvBackupList] = useState([]);
  const fileInputRef = useRef(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [showUserActionsMenu, setShowUserActionsMenu] = useState(null);
  const [showPhoneMenu, setShowPhoneMenu] = useState(null);
  const actionsMenuRef = useRef(null);
  const [showUserFileManager, setShowUserFileManager] = useState(null);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [selectedUserForInfo, setSelectedUserForInfo] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [availableDevices, setAvailableDevices] = useState([]);
  const [userActivity, setUserActivity] = useState({});
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  // View mode for the users list — persisted so admin's preference survives reloads.
  // Stale 'list' (a removed view) falls back to 'table'.
  const [userViewMode, setUserViewModeRaw] = useState(() => {
    try {
      const v = localStorage.getItem('admin_users_view');
      return v === 'cards' ? 'cards' : 'table';
    } catch { return 'table'; }
  });
  const setUserViewMode = (v) => {
    setUserViewModeRaw(v);
    try { localStorage.setItem('admin_users_view', v); } catch {}
  };
  // Multi-select role filter — empty array = show every role.
  const [userRoleFilter, setUserRoleFilter] = useState([]);
  // Which row's kebab menu is open (by user.id). Single instance global; click-out closes it.
  // Position is computed at open time so we can fixed-position the menu and flip it
  // upwards when there isn't enough room below — escapes overflow-clipped scrollers.
  const [openRowMenuId, setOpenRowMenuId] = useState(null);
  const [openRowMenuPos, setOpenRowMenuPos] = useState(null);
  const closeRowMenu = () => { setOpenRowMenuId(null); setOpenRowMenuPos(null); };
  const [showNewsEditor, setShowNewsEditor] = useState(false);
  const [newsContent, setNewsContent] = useState('');
  const [isEditingNews, setIsEditingNews] = useState(false);
  const [isLoadingNews, setIsLoadingNews] = useState(false);
  const [newsTempContent, setNewsTempContent] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // TV Manager is the BASELINE tier (owner, 2026-10-27): a new owner gets the
  // scope plus all three of its caps (Players / Files / Groups) ticked, and an
  // admin unticks what they don't want. Display Manager (F&B Menu / Info
  // Screen / Live Stream) stays off — those are opt-in extras.
  // Single source of truth: the Add User initializer and the reset-after-save
  // path both call this, which is how the two used to drift apart.
  const baselineOwnerAccess = () => {
    const access = emptyAccess();
    access.tv.enabled = true;
    access.tv.caps.players.enabled = true;
    access.tv.caps.files.enabled   = true;
    access.tv.caps.groups.enabled  = true;
    return access;
  };

  const [newUser, setNewUser] = useState(() => {
    const access = baselineOwnerAccess();
    return {
      username: '',
      password: '',
      email: '',
      fullname: '',
      phone: '',
      is_admin: false,
      user_type: 'regular',
      pro_expires_at: '',
      access,                           // source of truth for permissions
      role: 'owner',
      parent_user_id: '',
      can_create_subusers: false,  // staff feature removed 2026-10-27
    };
  });

  const [editedUser, setEditedUser] = useState({
    id: null,
    username: '',
    email: '',
    fullname: '',
    phone: '',
    is_admin: false,
    user_type: 'regular',
    access: emptyAccess(),
    pro_expires_at: '',
    new_password: ''
  });

  // Brand-access matrix state for managers/operators in the unified User Manager
  // modal. allBrands populated on first open; brandAccessRows holds the form
  // state; initialBrandAccessIds is the snapshot taken at modal open so we know
  // which rows need DELETE on save.
  const [allBrands, setAllBrands] = useState([]);
  const [brandAccessRows, setBrandAccessRows] = useState([]);
  const [initialBrandAccessIds, setInitialBrandAccessIds] = useState([]);
  // Branch-level restrict for branch_managers — list of branch_id this user has access to.
  // Empty = access to all branches under their granted brands. branchesByBrand
  // is a flat list of all branches the form needs to know about (cached lazily).
  const [branchesByBrand, setBranchesByBrand] = useState({});  // { [brand_id]: [branches] }
  const [branchAccessIds, setBranchAccessIds] = useState([]);  // currently-granted branch_ids
  const [initialBranchAccessIds, setInitialBranchAccessIds] = useState([]);

  const token = localStorage.getItem('token');
  const apiUrlBase = `${getRuntimeConfig().API_BASE_URL}/api`;
  
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
    
// Fetch news from backend
  const fetchNews = async () => {
    try {
      setIsLoadingNews(true);
      const response = await fetch('/api/news');
      
      if (response.ok) {
        const data = await response.json();
        setNewsContent(data.content || '');
        setNewsTempContent(data.content || '');
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
    } finally {
      setIsLoadingNews(false);
    }
  };

  // Save news to backend
  const saveNews = async () => {
    try {
      const response = await fetch('/api/news', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ content: newsTempContent })
      });

      if (response.ok) {
        setNewsContent(newsTempContent);
        setIsEditingNews(false);
        alert('✅ News updated successfully!');
      } else {
        throw new Error('Failed to save news');
      }
    } catch (err) {
      console.error('Error saving news:', err);
      alert('❌ Failed to save news. Please try again.');
    }
  };

  // Toggle news editor
  const toggleNewsEditor = () => {
    setShowNewsEditor(!showNewsEditor);
    if (!showNewsEditor && newsContent === '') {
      fetchNews();
    }
  };

  // Enter edit mode
  const enterNewsEditMode = () => {
    setNewsTempContent(newsContent);
    setIsEditingNews(true);
  };

  // Cancel edit
  const cancelNewsEdit = () => {
    setNewsTempContent(newsContent);
    setIsEditingNews(false);
  };

  // EXPOSE FUNCTIONS AND STATES TO PARENT (App.js) VIA REF
  useImperativeHandle(ref, () => ({
    handleAddUser: handleAddUser,
    handleBackupDatabase: handleBackupDatabase,
    handleRestoreDatabase: handleRestoreDatabase,
    handleServerBackup: handleServerBackup,
    handleServerRestore: handleServerRestore,
    backupInProgress: backupInProgress,
    restoreInProgress: restoreInProgress,
    srvBackupInProgress: srvBackupInProgress,
    srvRestoreInProgress: srvRestoreInProgress,
    openStorageEmpty: () => { setStorageInitialTab('empty'); setActiveTab('storage'); },
    switchTab: (tab) => setActiveTab(tab),
  }));

  // Fetch all users when component mounts (only for users tab)
  useEffect(() => {
    if (activeTab === 'users') {
      console.log('Fetching fresh user data for users tab');
      fetchUsers();
    }

    sessionStorage.setItem('adminPanelActive', 'true');

    return () => {
      sessionStorage.removeItem('adminPanelActive');
    };
  }, [activeTab]);

  // Consume cross-app handoff preset from menu-app (Edit Brand → click manager
  // / + Add Manager). Once users have loaded, open the matching add/edit modal.
  useEffect(() => {
    if (!users || users.length === 0) return;
    const raw = sessionStorage.getItem('pm_user_modal_preset');
    if (!raw) return;
    sessionStorage.removeItem('pm_user_modal_preset');
    let preset;
    try { preset = JSON.parse(raw); } catch { return; }
    setActiveTab('users');
    if (preset.mode === 'edit') {
      const target = users.find(u => u.id === preset.user_id || u.username === preset.username);
      if (target) handleEditUser(target);
    } else if (preset.mode === 'add') {
      setNewUser(prev => ({
        ...prev,
        role: preset.role || 'brand_manager',
        is_admin: false,
        user_type: 'regular',
        parent_user_id: preset.parent_user_id || '',
      }));
      // Pre-grant the brand if provided so the matrix shows it granted on first paint.
      if (preset.brand_id) {
        setBrandAccessRows([{
          brand_id: preset.brand_id, scopes: ['signage'],
          can_edit: true, can_delete: false,
        }]);
        setInitialBrandAccessIds([]);
      }
      setEditMode(false);
      setIsAddingUser(true);
      setShowAddUserModal(true);
    }
  }, [users]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Update current user when localStorage changes
  useEffect(() => {
    const updateCurrentUser = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUser(user);
        console.log('AdminPanel: Current user updated to:', user.username);
      } catch {
        setCurrentUser({});
      }
    };
    
    window.addEventListener('storage', updateCurrentUser);
    updateCurrentUser();
    
    return () => window.removeEventListener('storage', updateCurrentUser);
  }, []);

  // Handle clicking outside the actions menu and phone menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (showUserActionsMenu && 
          actionsMenuRef.current && !actionsMenuRef.current.contains(event.target) &&
          !event.target.closest('.user-card-header')
      ) {
        setShowUserActionsMenu(null);
      }
      
      if (showPhoneMenu &&
          !event.target.closest('.phone-menu-dropdown-global') &&
          !event.target.closest('.phone-button')
      ) {
        setShowPhoneMenu(null);
      }

      if (openRowMenuId &&
          !event.target.closest('.ap-row-menu') &&
          !event.target.closest('.ap-row-kebab') &&
          !event.target.closest('.ap-row')         /* row click reassigns the menu, don't pre-close */
      ) {
        closeRowMenu();
      }
    }

    const onScroll = () => { if (openRowMenuId) closeRowMenu(); };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [showUserActionsMenu, showPhoneMenu, actionsMenuRef, openRowMenuId]);

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const sc = document.querySelector('.sb-main') || window;
    const handleScroll = () => {
      const pos = sc === window ? window.pageYOffset : sc.scrollTop;
      setShowScrollTop(pos > 300);
    };
    sc.addEventListener('scroll', handleScroll, { passive: true });
    return () => sc.removeEventListener('scroll', handleScroll);
  }, []);

  // Enhanced WebSocket message handling for user activity updates
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'user_activity_update' && data.payload) {
          const { username, last_activity, is_online, user_id } = data.payload;
          
          console.log(`User activity update for: ${username}, online: ${is_online}`);
          
          setUsers(prevUsers => 
            prevUsers.map(user => 
              user.username === username 
                ? { 
                    ...user, 
                    last_login: last_activity,
                    is_online: is_online === true,
                    is_currently_online: is_online === true
                  }
                : user
            )
          );
          
          setUserActivity(prev => ({
            ...prev,
            [username]: last_activity
          }));
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    // Remove any existing listeners first
    if (window.wsConnection) {
      window.wsConnection.removeEventListener('message', handleWebSocketMessage);
    }

    // Add the listener only once
    if (window.wsConnection && window.wsConnection.readyState === 1) {
      window.wsConnection.addEventListener('message', handleWebSocketMessage);
      console.log('WebSocket listener attached for AdminPanel');
    } else {
      const checkConnection = setInterval(() => {
        if (window.wsConnection && window.wsConnection.readyState === 1) {
          window.wsConnection.addEventListener('message', handleWebSocketMessage);
          console.log('WebSocket listener attached after retry');
          clearInterval(checkConnection);
        }
      }, 1000);
      
      setTimeout(() => clearInterval(checkConnection), 10000);
    }
    
    return () => {
      if (window.wsConnection) {
        window.wsConnection.removeEventListener('message', handleWebSocketMessage);
        console.log('WebSocket listener removed from AdminPanel');
      }
    };
  }, []);

  // Toggle actions menu for users
  const toggleUserActionsMenu = (username, event) => {
    event.stopPropagation();
    
    setShowUserActionsMenu(null);
    
    if (showUserActionsMenu === username) {
      return;
    }
    
    setTimeout(() => {
      setShowUserActionsMenu(username);
      
      setTimeout(() => {
        const dropdown = document.querySelector('.actions-menu-dropdown');
        const headerElement = event.target.closest('.user-card-header');
        
        if (dropdown && headerElement) {
          const rect = headerElement.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          let top = rect.bottom + 5;
          let left = rect.left;
          
          const dropdownWidth = dropdown.offsetWidth;
          const dropdownHeight = dropdown.offsetHeight;
          
          if (left + dropdownWidth > viewportWidth - 10) {
            left = Math.max(10, viewportWidth - dropdownWidth - 10);
          }
          
          if (top + dropdownHeight > viewportHeight - 10) {
            top = rect.top - dropdownHeight - 5;
          }
          
          dropdown.style.top = `${top}px`;
          dropdown.style.left = `${left}px`;
        }
      }, 10);
    }, 50);
  };

  // Toggle phone menu
  const togglePhoneMenu = (username, event) => {
    event.stopPropagation();
    
    setShowPhoneMenu(null);
    
    if (showPhoneMenu === username) {
      return;
    }
    
    setTimeout(() => {
      setShowPhoneMenu(username);
      
      setTimeout(() => {
        const dropdown = document.querySelector('.phone-menu-dropdown-global');
        const phoneButton = event.target.closest('.phone-button');
        
        if (dropdown && phoneButton) {
          const rect = phoneButton.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          let top = rect.bottom + 5;
          let left = rect.left;
          
          const dropdownWidth = dropdown.offsetWidth;
          const dropdownHeight = dropdown.offsetHeight;
          
          if (left + dropdownWidth > viewportWidth - 10) {
            left = Math.max(10, viewportWidth - dropdownWidth - 10);
          }
          
          if (top + dropdownHeight > viewportHeight - 10) {
            top = rect.top - dropdownHeight - 5;
          }
          
          dropdown.style.top = `${top}px`;
          dropdown.style.left = `${left}px`;
        }
      }, 10);
    }, 50);
  };

  // Fetch users from API
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrlBase}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log(`AdminPanel: Current logged-in user is: ${currentUser.username}`);
      const updatedUsers = data.map(user => ({
        ...user,
        is_currently_online: user.is_online === true
      }));
      
      setUsers(updatedUsers);
      
      // IMPROVED: Use device_count from API response if available
      const permissions = {};
      updatedUsers.forEach(user => {
        if (user.device_count !== undefined) {
          permissions[user.username] = user.device_count;
        } else {
          // Fallback: fetch permissions separately if not in API
          fetchUserDevicePermissions(user.id, user.username);
        }
      });
      
      // Set permissions from API data
      if (Object.keys(permissions).length > 0) {
        setUserPermissions(prev => ({
          ...prev,
          ...permissions
        }));
      }
      
      setError(null);
      return updatedUsers;
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please check your connection and permissions.");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user device permissions
  const fetchUserDevicePermissions = async (userId, username) => {
    try {
      const response = await fetch(`${apiUrlBase}/admin/users/${userId}/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const permittedDevices = data.filter(d => d.has_permission).map(d => d.device_name);
        
        // IMPROVED: Get the actual count from the user's assigned devices
        // Use the API count if available, otherwise count from data
        const deviceCount = data.length > 0 && data[0].total_devices !== undefined 
          ? data[0].total_devices 
          : permittedDevices.length;
        
        console.log(`User ${username}: ${permittedDevices.length} devices from filtered data`);
        
        setUserPermissions(prev => ({
          ...prev,
          [username]: deviceCount
        }));
      }
    } catch (err) {
      console.error("Error fetching device permissions:", err);
    }
  };

  // Format last activity date in Cairo timezone
const formatLastActivity = (dateString) => {
  if (!dateString) return 'Never';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is invalid (e.g., if dateString was malformed)
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Use toLocaleString() for simplicity and reliability, forcing the Africa/Cairo timezone
    const formattedTime = date.toLocaleString('en-GB', {
      timeZone: 'Africa/Cairo',
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true 
    });
    
    // Convert the format from "dd/mm/yyyy, hh:mm am/pm" to "dd/mm/yyyy @ hh.mm am/pm"
    // The locale output varies, so we make one reliable replacement.
    
    // Example output from toLocaleString('en-GB'): "02/10/2025, 11:54 am"
    // We want: "02/10/2025 @ 11.54 am"
    
    const parts = formattedTime.split(', ');
    const datePart = parts[0]; // "02/10/2025"
    const timePart = parts[1]; // "11:54 am" or "11:54 p.m."
    
    const timeFormatted = timePart.replace(':', '.').replace(/ /g, '').toLowerCase(); // "11.54am"
    
    return `${datePart} @ ${timeFormatted}`;
    
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

  // Handle phone actions
  const handlePhoneAction = (phone, action) => {
    if (!phone) return;
    
    if (action === 'call') {
      window.open(`tel:${phone}`, '_self');
    } else if (action === 'whatsapp') {
      window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}`, '_blank');
    }
    
    setShowPhoneMenu(null);
  };

  // Handle adding a new user
  const handleAddUser = () => {
    setSelectedUser(null);
    setEditedUser({
      id: null,
      username: '',
      email: '',
      fullname: '',
      phone: '',
      is_admin: false,
      user_type: 'regular',
      access: emptyAccess(),
      new_password: ''
    });
    const resetAccess = baselineOwnerAccess();
    setNewUser({
      username: '',
      password: '',
      email: '',
      fullname: '',
      phone: '',
      is_admin: false,
      user_type: 'regular',
      pro_expires_at: '',
      access: resetAccess,
      role: 'owner',
      parent_user_id: '',
      can_create_subusers: false,  // staff feature removed 2026-10-27
    });
    setBrandAccessRows([]);
    setInitialBrandAccessIds([]);

    setEditMode(false);
    setIsAddingUser(true);
    setShowAddUserModal(true);
  };

  // Fetch the full brands list once when the modal opens (used for the
  // staff brand-access matrix). Cheap GET; result is reused.
  useEffect(() => {
    if (!showAddUserModal) return;
    if (allBrands.length > 0) return;
    fetch(`${apiUrlBase}/m/brands`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    })
      .then(r => r.ok ? r.json() : [])
      .then(setAllBrands)
      .catch(() => {});
  }, [showAddUserModal]);  // eslint-disable-line react-hooks/exhaustive-deps

  // For branch_managers + operators: lazy-fetch branches for each granted brand
  // so the dropdown chooser can render its options.
  useEffect(() => {
    if (!showAddUserModal) return;
    const cur  = editMode ? editedUser : newUser;
    const role = cur.role || (cur.is_admin ? 'admin' : 'owner');
    if (role !== 'branch_manager' && role !== 'operator') return;
    brandAccessRows.forEach(r => {
      if (!branchesByBrand[r.brand_id]) ensureBrandBranches(r.brand_id);
    });
  }, [showAddUserModal, brandAccessRows, editMode, editedUser.role, newUser.role]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Per-brand "branches dropdown is open" state for the matrix.
  const [openBranchPicker, setOpenBranchPicker] = useState(null);  // brand_id or null

  // Per-role default privileges applied when toggling a brand into the matrix.
  const ROLE_DEFAULT_PRIVS = {
    brand_manager:  { scopes: ['signage','qr_menu','pos'], can_edit: true,  can_delete: true  },
    branch_manager: { scopes: ['signage','qr_menu','pos'], can_edit: true,  can_delete: false },
    operator:       { scopes: ['signage','qr_menu'],       can_edit: false, can_delete: false },
  };

  // Lazy-fetch branches for a given brand (used by the branch-manager picker).
  const ensureBrandBranches = async (brandId) => {
    if (branchesByBrand[brandId]) return branchesByBrand[brandId];
    try {
      const res = await fetch(`${apiUrlBase}/m/branches?brand_id=${brandId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const branches = await res.json();
      setBranchesByBrand(prev => ({ ...prev, [brandId]: branches }));
      return branches;
    } catch { return []; }
  };

  // Flush brand-access (and branch-access for branch_managers) after user save.
  const flushBrandAccess = async (userId, role) => {
    const currentBrandIds = new Set(brandAccessRows.map(r => r.brand_id));
    // Upsert each brand row in the form
    for (const row of brandAccessRows) {
      await fetch(`${apiUrlBase}/m/users/${userId}/brand-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          brand_id: row.brand_id,
          scopes: row.scopes,
          can_edit: row.can_edit,
          can_delete: row.can_delete,
        }),
      });
    }
    // Delete brand rows that were in the initial snapshot but no longer in the form
    for (const id of initialBrandAccessIds) {
      if (!currentBrandIds.has(id)) {
        await fetch(`${apiUrlBase}/m/users/${userId}/brand-access/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
      }
    }

    // For branch_managers + operators: flush branch-level restrict too.
    if (role === 'branch_manager' || role === 'operator') {
      const currentBranchIds = new Set(branchAccessIds);
      // Upsert
      for (const branchId of branchAccessIds) {
        await fetch(`${apiUrlBase}/m/users/${userId}/branch-access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ branch_id: branchId }),
        });
      }
      // Revoke removed
      for (const id of initialBranchAccessIds) {
        if (!currentBranchIds.has(id)) {
          await fetch(`${apiUrlBase}/m/users/${userId}/branch-access/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          });
        }
      }
    } else if (initialBranchAccessIds.length > 0) {
      // brand_manager / non-staff: clear stale branch-access rows so they don't
      // accidentally restrict the user after a role change.
      for (const id of initialBranchAccessIds) {
        await fetch(`${apiUrlBase}/m/users/${userId}/branch-access/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
      }
    }
  };

  // Handle creating a new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const createResponse = await fetch(`${apiUrlBase}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newUser,
          // 2026-05-18 — `access` JSONB is the source of truth for permission
          // gates. Backend derives the legacy pro_features / feature_expires_at
          // from it during dual-write.
          access: newUser.access || {},
          pro_features: newUser.pro_features || [],
          role: newUser.role || (newUser.is_admin ? 'admin' : 'owner'),
          parent_user_id: newUser.parent_user_id || null,
          can_create_subusers: false,  // staff feature removed 2026-10-27 — nobody creates sub-users
          feature_expires_at: newUser.feature_expires_at || {},
        }),
        credentials: 'include'
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        // Friendly handling for duplicate usernames: pull alternatives.
        if (createResponse.status === 400 && /already exists/i.test(errorData.error || '')) {
          try {
            const sugRes = await fetch(`${apiUrlBase}/admin/users/check-username?u=${encodeURIComponent(newUser.username)}`, {
              headers: { Authorization: `Bearer ${token}` },
              credentials: 'include',
            });
            const sug = sugRes.ok ? await sugRes.json() : { suggestions: [] };
            const list = (sug.suggestions || []).slice(0, 4);
            const pick = list.length > 0
              ? window.prompt(
                  `Username "${newUser.username}" is already taken.\n\nTry one of these (click OK to use the first, or edit the field):\n  ${list.join('\n  ')}\n`,
                  list[0]
                )
              : null;
            if (pick && pick !== newUser.username) {
              setNewUser(prev => ({ ...prev, username: pick }));
            }
          } catch (_) {}
          return;
        }
        throw new Error(errorData.error || `HTTP error! status: ${createResponse.status}`);
      }

      const userData = await createResponse.json();
      console.log('User created successfully:', userData);

      // For staff users, flush any brand-access rows the admin set in the modal.
      const createdRole = userData?.user?.role || (userData?.user?.is_admin ? 'admin' : 'owner');
      if (['brand_manager','branch_manager','operator'].includes(createdRole)) {
        try { await flushBrandAccess(userData.user.id, createdRole); }
        catch (e) { console.warn('Brand-access save failed:', e); }
      }

      setShowAddUserModal(false);
      setIsAddingUser(false);
      setNewUser({
        username: '',
        password: '',
        email: '',
        fullname: '',
        phone: '',
        is_admin: false,
        user_type: 'regular',
        pro_features: []
      });
      
      await fetchUsers();
      window.refreshOwners?.();

      setSuccessMessage('User successfully added');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (err) {
      console.error("Error creating user:", err);
      alert(`Failed to create user: ${err.message}`);
    }
  };

  // Handle editing a user
 const handleEditUser = (user) => {
    setIsAddingUser(false);

    setSelectedUser(user);
    // 2026-05-18 — hydrate the hierarchical `access` tree. Backend returns
    // a populated tree (the /api/admin/users endpoint backfills with
    // emptyAccess() for any user whose `access` is still null). Fall back
    // to emptyAccess() so the Feature Grid always has something to render
    // against — never a blank object that would crash scopesByOrder().
    const userAccess = (user.access && typeof user.access === 'object' && Object.keys(user.access).length)
      ? user.access
      : emptyAccess();
    setEditedUser({
      id: user.id,
      username: user.username,
      email: user.email || '',
      fullname: user.fullname || '',
      phone: user.phone || '',
      is_admin: user.is_admin || false,
      user_type: user.user_type || 'regular',
      access: userAccess,
      pro_features: Array.isArray(user.pro_features) ? user.pro_features : [],
      pro_expires_at: user.pro_expires_at ? user.pro_expires_at.slice(0, 10) : '',
      role: user.role || (user.is_admin ? 'admin' : 'owner'),
      parent_user_id: user.parent_user_id || '',
      can_create_subusers: false,  // staff feature removed 2026-10-27
      feature_expires_at: user.feature_expires_at || {},
      new_password: ''
    });

    const role = user.role || (user.is_admin ? 'admin' : 'owner');

    // For staff users: fetch existing brand-access grants to populate the matrix.
    setBrandAccessRows([]);
    setInitialBrandAccessIds([]);
    setBranchAccessIds([]);
    setInitialBranchAccessIds([]);
    if (['brand_manager','branch_manager','operator'].includes(role)) {
      fetch(`${apiUrlBase}/m/users/${user.id}/brand-access`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      })
        .then(r => r.ok ? r.json() : [])
        .then(rows => {
          setBrandAccessRows(rows.map(r => ({
            brand_id: r.brand_id, scopes: r.scopes || [], can_edit: !!r.can_edit, can_delete: !!r.can_delete,
          })));
          setInitialBrandAccessIds(rows.map(r => r.brand_id));
        })
        .catch(() => {});

      // Branch-level restrict only meaningful for branch_managers, but we
      // still load existing rows so an admin demoting from branch_manager
      // → operator can see what was granted.
      fetch(`${apiUrlBase}/m/users/${user.id}/branch-access`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      })
        .then(r => r.ok ? r.json() : [])
        .then(rows => {
          setBranchAccessIds(rows.map(r => r.branch_id));
          setInitialBranchAccessIds(rows.map(r => r.branch_id));
        })
        .catch(() => {});
    }

    setEditMode(true);
    setShowAddUserModal(true);
    setShowUserActionsMenu(null);
  };

  // Handle updating a user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateResponse = await fetch(`${apiUrlBase}/admin/users/${editedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: editedUser.email,
          fullname: editedUser.fullname,
          phone: editedUser.phone,
          is_admin: editedUser.is_admin,
          user_type: editedUser.user_type,
          // 2026-05-18 — `access` JSONB is the source of truth for permission
          // gates. Backend derives the legacy pro_features / feature_expires_at
          // from it during dual-write.
          access: editedUser.access || {},
          pro_features: editedUser.pro_features || [],
          pro_expires_at: editedUser.user_type === 'pro' ? (editedUser.pro_expires_at || null) : null,
          role: editedUser.role || (editedUser.is_admin ? 'admin' : 'owner'),
          parent_user_id: editedUser.parent_user_id || null,
          can_create_subusers: false,  // staff feature removed 2026-10-27 — nobody creates sub-users
          feature_expires_at: editedUser.feature_expires_at || {},
        }),
        credentials: 'include'
      });
      
      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.error || `HTTP error! status: ${updateResponse.status}`);
      }

      const editedRole = editedUser.role || (editedUser.is_admin ? 'admin' : 'owner');

      // Staff: flush brand-access (and branch-access for branch_managers) after
      // the user update succeeded.
      if (['brand_manager','branch_manager','operator'].includes(editedRole)) {
        try { await flushBrandAccess(editedUser.id, editedRole); }
        catch (e) { console.warn('Brand-access save failed:', e); }
      }

      if (editedUser.new_password) {
        const resetResponse = await fetch(`${apiUrlBase}/admin/users/${editedUser.id}/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ new_password: editedUser.new_password }),
          credentials: 'include'
        });
        
        if (!resetResponse.ok) {
          const errorData = await resetResponse.json();
          throw new Error(errorData.error || `HTTP error! status: ${resetResponse.status}`);
        }
      }

      // If this was a staff edit with a parent_user_id, snapshot the parent
      // so we can re-open the owner's edit modal after fetchUsers refreshes.
      const returnToParentId = editedUser.parent_user_id || null;

      setShowAddUserModal(false);
      setEditMode(false);
      setSelectedUser(null);

      const refreshed = await fetchUsers();
      window.refreshOwners?.();

      // If admin just updated their own account, refresh sidebar user data
      if (editedUser.id === currentUser.id) {
        window.dispatchEvent(new Event('pm:refresh-user'));
      }

      alert('User updated successfully' + (editedUser.new_password ? ' with new password' : ''));
      setEditedUser(prev => ({...prev, new_password: ''}));

      // Return to owner card: if the edited user has a parent owner, re-open
      // the parent's Edit modal so the admin lands back where they came from.
      if (returnToParentId) {
        // Look up the freshest parent row from the new users list.
        const list = Array.isArray(refreshed) ? refreshed : users;
        const parent = list.find(u => u.id === returnToParentId);
        if (parent) setTimeout(() => handleEditUser(parent), 60);
      }

    } catch (err) {
      console.error("Error updating user:", err);
      alert(`Failed to update user: ${err.message}`);
    }
  };

  // 2026-10-27 — updateUserDevicePermissions()/handleToggleDevicePermission()
  // removed: they only served the Device Permissions section of the Edit User
  // modal, which is gone now (device↔owner assignment happens from the player
  // card / Adopt flow instead — see the comment where that section used to be).

  // Toggle suspend on any user — used by table/list/kebab row actions.
  // Failure mode: log + alert so admin notices.
  const handleToggleSuspend = async (user) => {
    try {
      const res = await fetch(`${apiUrlBase}/admin/users/${user.id}/suspend`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ is_suspended: !user.is_suspended }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchUsers();
    } catch (err) {
      console.warn('suspend toggle failed', err);
      alert(`Failed to ${user.is_suspended ? 'unsuspend' : 'suspend'} user.`);
    }
  };

  // Handle deleting a user
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${apiUrlBase}/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      setShowUserActionsMenu(null);
      await fetchUsers();

    } catch (err) {
      console.error("Error deleting user:", err);
      alert(`Failed to delete user: ${err.message}`);
    }
  };

  // Handle showing user info
  const handleShowUserInfo = (user) => {
    setSelectedUserForInfo(user);
    setShowUserInfoModal(true);
    setShowUserActionsMenu(null);
  };

  // Handle database backup
  const handleBackupDatabase = async () => {
    setBackupInProgress(true);
    try {
      const response = await fetch(`${apiUrlBase}/admin/backup`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create backup: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      
      const date = new Date().toISOString().split('T')[0];
      a.download = `signage_monitor_backup_${date}.sql`;
      a.href = url;
      a.click();
      
      window.URL.revokeObjectURL(url);
      alert('Database backup created and downloaded successfully!');
    } catch (err) {
      console.error("Error backing up database:", err);
      alert(`Failed to backup database: ${err.message}`);
    } finally {
      setBackupInProgress(false);
    }
  };

  // Handle database restore
  const handleRestoreDatabase = async () => {
    const tempFileInput = document.createElement('input');
    tempFileInput.type = 'file';
    tempFileInput.accept = '.sql';
    tempFileInput.style.display = 'none';
    
    tempFileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      if (!window.confirm('Are you sure you want to restore the database? This will overwrite all current data.')) {
        return;
      }

      setRestoreInProgress(true);
      
      try {
        const formData = new FormData();
        formData.append('backup_file', file);
        
        const response = await fetch(`${apiUrlBase}/admin/restore`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
          credentials: 'include'
        });
        
        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            try {
              const errorText = await response.text();
              if (errorText) {
                errorMessage = `Server error: ${errorText.substring(0, 100)}...`;
              }
            } catch (textError) {
              // Keep the original error message
            }
          }
          throw new Error(errorMessage);
        }
        
        const result = await response.json();
        alert('Database restored successfully. The page will reload to reflect changes.');
        window.location.reload();
        
      } catch (err) {
        console.error("Error restoring database:", err);
        alert(`Failed to restore database: ${err.message}`);
      } finally {
        setRestoreInProgress(false);
      }
    };
    
    document.body.appendChild(tempFileInput);
    tempFileInput.click();
    document.body.removeChild(tempFileInput);
  };

  // Handle server backup
  const handleServerBackup = async () => {
    setSrvBackupInProgress(true);
    try {
      const response = await fetch(`${apiUrlBase}/backup/srv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      alert(`Server backup created successfully: ${result.filename}`);
      
    } catch (err) {
      console.error('Error creating server backup:', err);
      alert(`Failed to create server backup: ${err.message}`);
    } finally {
      setSrvBackupInProgress(false);
    }
  };

  // Handle server restore
  const handleServerRestore = async () => {
    try {
      const response = await fetch(`${apiUrlBase}/backup/srv/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const backupList = data.backups || [];
        
        if (backupList.length > 0) {
          const choice = window.confirm(
            `Found ${backupList.length} existing server backup(s).\n\n` +
            `Click "OK" to choose from existing backups\n` +
            `Click "Cancel" to upload a new backup file`
          );
          
          if (choice) {
            const backupNames = backupList.map(backup => 
              `${backup.filename} (${backup.size}, ${backup.date})`
            );
            const selectedBackup = prompt(
              `Select a backup file to restore:\n\n${backupNames.map((name, i) => `${i + 1}. ${name}`).join('\n')}\n\nEnter the number (1-${backupNames.length}):`
            );
            
            if (!selectedBackup || isNaN(selectedBackup) || selectedBackup < 1 || selectedBackup > backupNames.length) {
              return;
            }
            
            const filename = backupList[parseInt(selectedBackup) - 1].filename;
            
            if (!window.confirm(`Are you sure you want to restore from "${filename}"? This will overwrite current server files.`)) {
              return;
            }
            
            setSrvRestoreInProgress(true);
            
            try {
              const restoreResponse = await fetch(`${apiUrlBase}/backup/srv/restore`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ filename }),
                credentials: 'include'
              });
              
              if (!restoreResponse.ok) {
                const errorData = await restoreResponse.json();
                throw new Error(errorData.error || `HTTP error! status: ${restoreResponse.status}`);
              }
              
              alert('Server restored successfully from backup.');
              
            } catch (err) {
              console.error("Error restoring server:", err);
              alert(`Failed to restore server: ${err.message}`);
            } finally {
              setSrvRestoreInProgress(false);
            }
            
            return;
          }
        }
      }
    } catch (err) {
      console.log("Could not fetch backup list, proceeding with file upload option");
    }
    
    const tempFileInput = document.createElement('input');
    tempFileInput.type = 'file';
    tempFileInput.accept = '.gz,.tar.gz';
    tempFileInput.style.display = 'none';
    
    tempFileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      if (!window.confirm('Are you sure you want to restore the server from uploaded file? This will overwrite current server files.')) {
        return;
      }

      setSrvRestoreInProgress(true);
      
      try {
        const formData = new FormData();
        formData.append('srv_backup_file', file);
        
        const response = await fetch(`${apiUrlBase}/backup/srv/restore/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
          credentials: 'include'
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }
        
        alert('Server restored successfully from uploaded backup file.');
        
      } catch (err) {
        console.error("Error restoring server from upload:", err);
        alert(`Failed to restore server: ${err.message}`);
      } finally {
        setSrvRestoreInProgress(false);
      }
    };
    
    document.body.appendChild(tempFileInput);
    tempFileInput.click();
    document.body.removeChild(tempFileInput);
  };

  if (isLoading && activeTab === 'users') {
    return (
      <div className="admin-panel">
        <div className="admin-loading">Loading users...</div>
      </div>
    );
  }

  if (error && activeTab === 'users') {
    return (
      <div className="admin-panel">
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <style>{`@keyframes ap-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.55;transform:scale(1.07)} }`}</style>
      {successMessage && (
        <div className="admin-success-toast">
          {successMessage}
        </div>
      )}
      {/* Tab navigation lives in the Sidebar's Admin Panel submenu now —
          User Control / Brands / Branches / Storage Monitor / Backup &
          Restore. The sidebar calls adminPanelRef.current.switchTab(...)
          to flip activeTab here without re-rendering the whole page. */}

      {/* Tab Content */}
      <div className="admin-tab-content">
        {activeTab === 'users' && (
  <div className="users-content">
    {/* Users Status Card - Horizontal Layout */}
    <div className="users-status-header">
      <div className="users-status-card">

        {/* ── Stat chips (clickable filters) ── */}
        {(() => {
          // Counters count every user shown in the list (admins + owners + staff),
          // so the totals stay honest when the role filter is widened.
          const cOnline  = users.filter(u => getUserStatusType(u) === 'online').length;
          const cOffline = users.filter(u => getUserStatusType(u) === 'offline-recent').length;
          const cNever   = users.filter(u => getUserStatusType(u) === 'never-logged').length;
          const cPro     = users.filter(u => u.user_type === 'pro').length;
          const cTotal   = users.length;

          const chips = [
            { key: 'all',           label: 'Total',   count: cTotal,   dot: null,      bg: '#F1F5F9', activeBg: '#334155', color: '#475569', activeColor: '#fff', border: '#CBD5E1', activeBorder: '#334155' },
            { key: 'online',        label: 'Online',  count: cOnline,  dot: '#2f80ed', bg: '#EFF6FF', activeBg: '#2f80ed', color: '#2f80ed', activeColor: '#fff', border: '#BFDBFE', activeBorder: '#2f80ed' },
            { key: 'offline-recent',label: 'Offline', count: cOffline, dot: '#60A5FA', bg: '#F0F9FF', activeBg: '#60A5FA', color: '#3B82F6', activeColor: '#fff', border: '#BAE6FD', activeBorder: '#60A5FA' },
            { key: 'never-logged',  label: 'Never',   count: cNever,   dot: '#EF4444', bg: '#FEF2F2', activeBg: '#EF4444', color: '#EF4444', activeColor: '#fff', border: '#FECACA', activeBorder: '#EF4444' },
            { key: 'pro',           label: 'Pro',     count: cPro,     dot: '#F97316', bg: '#FFF7ED', activeBg: '#F97316', color: '#EA580C', activeColor: '#fff', border: '#FED7AA', activeBorder: '#F97316' },
          ];

          return (
            <div className="ap-status-chips" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
              {chips.map(c => {
                const active = userStatusFilter === c.key;
                return (
                  <button key={c.key} onClick={() => setUserStatusFilter(c.key)}
                    title={`Filter: ${c.label}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '5px 10px', borderRadius: 20, cursor: 'pointer',
                      minWidth: 100,
                      border: `1.5px solid ${active ? c.activeBorder : c.border}`,
                      background: active ? c.activeBg : c.bg,
                      color: active ? c.activeColor : c.color,
                      fontWeight: active ? 700 : 500,
                      fontSize: 12,
                      transition: 'all 0.15s',
                      boxShadow: active ? `0 2px 8px ${c.activeBg}55` : 'none',
                    }}>
                    {c.dot
                      ? <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : c.dot, flexShrink: 0, display: 'inline-block' }} />
                      : <span style={{ fontSize: 11, lineHeight: 1 }}>👥</span>
                    }
                    <span style={{ letterSpacing: 0.2 }}>{c.label}</span>
                    <span style={{
                      fontWeight: 800, fontSize: 13,
                      background: active ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.07)',
                      borderRadius: 10, padding: '0px 6px', lineHeight: '18px',
                    }}>{c.count}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}
        
{/* Right side - Search and News Button */}
        <div className="users-search-container">
          <input
            type="text"
            placeholder="Search users..."
            className="users-search-input"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          
          {/* News Button */}
          <button
            className="add-user-button"
            onClick={handleAddUser}
            title="Add New User"
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add User
          </button>
          <button 
            className={`news-button ${showNewsEditor ? 'active' : ''}`}
            onClick={toggleNewsEditor}
            title="Manage News"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"></path>
              <path d="M18 14h-8"></path>
              <path d="M15 18h-5"></path>
              <path d="M10 6h8v4h-8V6Z"></path>
            </svg>
            {showNewsEditor ? 'Close' : 'News'}
          </button>
        </div>

        {/* ── Row 2: role filter (left) + view-mode toggle (right) ── */}
        {(() => {
          const ROLE_CHIPS = [
            { key: 'admin',          label: 'Admin',      bg: '#FEF2F2', color: '#B91C1C', border: '#FCA5A5' },
            { key: 'owner',          label: 'Owner',      bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
            { key: 'brand_manager',  label: 'Brand Mgr',  bg: '#EEF2FF', color: '#3730A3', border: '#A5B4FC' },
            { key: 'branch_manager', label: 'Branch Mgr', bg: '#ECFEFF', color: '#155E75', border: '#67E8F9' },
            { key: 'operator',       label: 'Operator',   bg: '#F0FDF4', color: '#166534', border: '#86EFAC' },
          ];
          const countByRole = (key) => users.filter(u => (u.role || (u.is_admin ? 'admin' : 'owner')) === key).length;
          const toggleRole = (key) => {
            setUserRoleFilter(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
          };
          const VIEWS = [
            { key: 'table', label: 'Table',
              icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 6h18M3 12h18M3 18h18"/></svg> },
            { key: 'cards', label: 'Cards',
              icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3"  width="8" height="8" rx="1"/><rect x="13" y="3"  width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg> },
          ];
          return (
            <div className="users-filter-row" style={{
              flexBasis: '100%', width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
              padding: '8px 0 0', borderTop: '1px dashed #E5E7EB', marginTop: 8,
            }}>
              {/* Role multi-select chips */}
              <div className="ap-role-chips" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: 0.6, marginRight: 4 }}>ROLE</span>
                <button
                  type="button"
                  onClick={() => setUserRoleFilter([])}
                  title="Show every role"
                  style={{
                    padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    border: `1.5px solid ${userRoleFilter.length === 0 ? '#334155' : '#CBD5E1'}`,
                    background: userRoleFilter.length === 0 ? '#334155' : '#F8FAFC',
                    color: userRoleFilter.length === 0 ? '#fff' : '#475569',
                    transition: 'all 0.15s',
                  }}
                >All</button>
                {ROLE_CHIPS.map(c => {
                  const active = userRoleFilter.includes(c.key);
                  const count  = countByRole(c.key);
                  return (
                    <button key={c.key} type="button" onClick={() => toggleRole(c.key)}
                      title={`Toggle ${c.label}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        border: `1.5px solid ${c.border}`,
                        background: active ? c.color : c.bg,
                        color: active ? '#fff' : c.color,
                        boxShadow: active ? `0 1px 4px ${c.color}55` : 'none',
                        transition: 'all 0.15s',
                      }}>
                      <span>{c.label}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 800,
                        background: active ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.06)',
                        color: active ? '#fff' : c.color,
                        borderRadius: 8, padding: '0 5px', lineHeight: '14px',
                      }}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* View-mode segmented toggle */}
              <div className="ap-view-toggle" style={{
                display: 'inline-flex', background: '#F1F5F9',
                border: '1px solid #CBD5E1', borderRadius: 8, padding: 2, gap: 0,
              }}>
                {VIEWS.map(v => {
                  const active = userViewMode === v.key;
                  return (
                    <button key={v.key} type="button" onClick={() => setUserViewMode(v.key)}
                      title={`${v.label} view`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        border: 'none',
                        background: active ? '#fff' : 'transparent',
                        color:      active ? '#1D4ED8' : '#475569',
                        boxShadow:  active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                        transition: 'all 0.15s',
                      }}>
                      {v.icon}
                      <span>{v.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>

    {/* News Editor Section */}
    {showNewsEditor && (
      <div className="news-editor-panel">
        <div className="news-editor-header">
          <h3>
            <span>📰</span>
            Login Page News Manager
          </h3>
          <div className="news-editor-buttons">
            {!isEditingNews ? (
              <button className="btn-edit-news" onClick={enterNewsEditMode}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit
              </button>
            ) : (
              <>
                <button 
                  className="btn-format-rtl"
                  onClick={() => {
                    const textarea = document.querySelector('.news-textarea');
                    if (textarea) {
                      textarea.style.direction = 'rtl';
                      textarea.style.textAlign = 'right';
                    }
                  }}
                  title="Arabic (Right to Left)"
                >
                  عربي
                </button>
                <button 
                  className="btn-format-ltr"
                  onClick={() => {
                    const textarea = document.querySelector('.news-textarea');
                    if (textarea) {
                      textarea.style.direction = 'ltr';
                      textarea.style.textAlign = 'left';
                    }
                  }}
                  title="English (Left to Right)"
                >
                  EN
                </button>
                <button className="btn-save-news" onClick={saveNews}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Save
                </button>
                <button className="btn-cancel-news" onClick={cancelNewsEdit}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        <div className="news-editor-content">
          {isEditingNews ? (
            <>
              <textarea
                className="news-textarea"
                value={newsTempContent}
                onChange={(e) => setNewsTempContent(e.target.value)}
                placeholder="Paste or type your news content here..."
              />
              
              <div className="news-help-text">
                <strong>📝 Formatting Guide:</strong><br/>
                • Use emojis for icons (📅 🎉 ⚠️ 📚 💡 🔔)<br/>
                • First line: Date with 📅<br/>
                • Second line: Title with emoji icon<br/>
                • Third line: Description text<br/>
                • Separate news items with "---"<br/><br/>
                <div className="news-help-example">
                  📅 January 11, 2026<br/>
                  🎉 Version 2.5 Released<br/>
                  Description text here...<br/>
                  <br/>
                  ---<br/>
                  <br/>
                  📅 January 8, 2026<br/>
                  ⚠️ Next News Item<br/>
                  Description here...
                </div>
              </div>
            </>
          ) : (
            <div className="news-display">
              {isLoadingNews ? (
                <div className="news-loading">Loading news...</div>
              ) : newsContent ? (
                <pre className="news-preview">{newsContent}</pre>
              ) : (
                <div className="news-empty">No news content yet. Click Edit to add news.</div>
              )}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Users list — three views (Table | List | Cards) share the same
        filter pipeline and the same decorator so the per-user metadata
        (role colour, status, parent, etc.) lives in one place. */}
    {(() => {
      const ROLE_ORDER = { admin: 0, owner: 1, brand_manager: 2, branch_manager: 3, operator: 4 };
      const roleOf = (u) => u.role || (u.is_admin ? 'admin' : 'owner');
      const matchesFilters = (user) => {
        const matchesText = !filterText ||
          user.username.toLowerCase().includes(filterText.toLowerCase()) ||
          (user.fullname && user.fullname.toLowerCase().includes(filterText.toLowerCase()));
        const statusType = getUserStatusType(user);
        const matchesStatus = userStatusFilter === 'all'
          || userStatusFilter === statusType
          || (userStatusFilter === 'pro' && user.user_type === 'pro');
        const matchesRole = userRoleFilter.length === 0 || userRoleFilter.includes(roleOf(user));
        return matchesText && matchesStatus && matchesRole;
      };
      const filtered = users.filter(matchesFilters);
      const usersById = Object.fromEntries(users.map(u => [u.id, u]));
      // Sort: hierarchy-aware — admins first, then each owner immediately
      // followed by their staff (brand_managers → branch_managers → operators,
      // each sub-group alphabetised by username). Orphan staff appear at end.
      const sortedFlat = (() => {
        const cmpName = (a, b) => (a.username || '').localeCompare(b.username || '', undefined, { sensitivity: 'base' });
        const admins = filtered.filter(u => roleOf(u) === 'admin').sort(cmpName);
        const owners = filtered.filter(u => roleOf(u) === 'owner').sort(cmpName);
        const STAFF_SET = new Set(['brand_manager', 'branch_manager', 'operator']);
        const staff = filtered.filter(u => STAFF_SET.has(roleOf(u)));
        const staffByOwnerId = {};
        staff.forEach(s => {
          const pid = s.parent_user_id;
          if (!staffByOwnerId[pid]) staffByOwnerId[pid] = [];
          staffByOwnerId[pid].push(s);
        });
        const STAFF_ORDER = { brand_manager: 0, branch_manager: 1, operator: 2 };
        Object.keys(staffByOwnerId).forEach(k => {
          staffByOwnerId[k].sort((a, b) => {
            const ra = STAFF_ORDER[roleOf(a)] ?? 99;
            const rb = STAFF_ORDER[roleOf(b)] ?? 99;
            if (ra !== rb) return ra - rb;
            return cmpName(a, b);
          });
        });
        const out = [...admins];
        const seenStaffIds = new Set();
        owners.forEach(o => {
          out.push(o);
          (staffByOwnerId[o.id] || []).forEach(s => { out.push(s); seenStaffIds.add(s.id); });
        });
        // Any staff whose owner isn't in the filtered list (or has no parent)
        // tail-appends in role-then-name order.
        const orphans = staff.filter(s => !seenStaffIds.has(s.id))
          .sort((a, b) => {
            const ra = STAFF_ORDER[roleOf(a)] ?? 99;
            const rb = STAFF_ORDER[roleOf(b)] ?? 99;
            if (ra !== rb) return ra - rb;
            return cmpName(a, b);
          });
        out.push(...orphans);
        return out;
      })();
      const sectionTitleStyle = {
        fontSize: 14, fontWeight: 800, color: '#374151', letterSpacing: 0.5,
        padding: '14px 4px 6px', borderBottom: '1.5px solid #E5E7EB', marginBottom: 8,
        display: 'flex', alignItems: 'center', gap: 8,
      };

      // ── Per-user metadata derivation shared by all three views ──
      const STATUS_ACCENT = {
        'online':         '#2f80ed',
        'offline-recent': '#60A5FA',
        'never-logged':   '#EF4444',
      };
      const roleBadgeFor = (user) => {
        switch (user.role) {
          case 'admin':           return { label: 'ADMIN',          bg: '#FEF2F2', color: '#B91C1C', border: '#FCA5A5' };
          case 'brand_manager':   return { label: 'BRAND MGR',      bg: '#EEF2FF', color: '#3730A3', border: '#A5B4FC' };
          case 'branch_manager':  return { label: 'BRANCH MGR',     bg: '#ECFEFF', color: '#155E75', border: '#67E8F9' };
          case 'operator':        return { label: 'OPERATOR',       bg: '#F0FDF4', color: '#166534', border: '#86EFAC' };
          case 'owner':
            if (user.user_type === 'pro')   return { label: 'OWNER · PRO', bg: '#FFF7ED', color: '#C2410C', border: '#FDBA74' };
            return { label: 'OWNER', bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' };
          default:
            if (user.user_type === 'admin') return { label: 'ADMIN', bg: '#FEF2F2', color: '#B91C1C', border: '#FCA5A5' };
            if (user.user_type === 'pro')   return { label: 'PRO',   bg: '#FFF7ED', color: '#C2410C', border: '#FDBA74' };
            return { label: 'USER',  bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' };
        }
      };
      const decorate = (user) => {
        const statusType = getUserStatusType(user);
        const accent = STATUS_ACCENT[statusType] || '#60A5FA';
        const roleType = roleBadgeFor(user);
        const proDaysLeft = (user.user_type === 'pro' && user.pro_expires_at)
          ? Math.ceil((new Date(user.pro_expires_at) - new Date()) / 86400000) : null;
        const initials = (user.username || '?').slice(0, 2).toUpperCase();
        const brandCount  = user.brand_count  ?? 0;
        const branchCount = user.branch_count ?? 0;
        const deviceCount = (userPermissions[user.username] || 0) || user.device_count || 0;
        const parent = user.parent_user_id ? usersById[user.parent_user_id] : null;
        // Avatar logo: staff inherit their owner's brand_logo (since they don't
        // own brands themselves). Owners use their own brand_logo. Admins fall
        // back to initials. usernameInitial is the 1-char fallback badge.
        const role = user.role;
        const isStaff = role === 'brand_manager' || role === 'branch_manager' || role === 'operator';
        const logoUrl = user.brand_logo || (isStaff && parent ? parent.brand_logo : null) || null;
        const usernameInitial = (user.username || '?').slice(0, 1).toUpperCase();
        // Plain-language role text — uses the parent owner's username as the
        // brand label (which is how most accounts are set up here).
        const ownerLabel = parent?.username || '';
        let roleText;
        switch (role) {
          case 'admin':          roleText = 'Admin'; break;
          case 'owner':          roleText = user.user_type === 'pro' ? 'Owner · Pro' : 'Owner'; break;
          case 'brand_manager':  roleText = ownerLabel ? `${ownerLabel} manager` : 'Brand manager'; break;
          case 'branch_manager': roleText = ownerLabel ? `branch ${ownerLabel} manager` : 'Branch manager'; break;
          case 'operator':       roleText = ownerLabel ? `${ownerLabel} operator` : 'Operator'; break;
          default:               roleText = roleType.label;
        }
        return {
          statusType, accent, roleType, roleText, proDaysLeft, initials, usernameInitial,
          brandCount, branchCount, deviceCount, parent, logoUrl, isStaff,
        };
      };
      const navUserFn = (user, kind) => {
        window.dispatchEvent(new CustomEvent('pm:nav', {
          detail: { kind, userId: user.id, username: user.username, brandId: user.first_brand_id, brandCount: user.brand_count ?? 0 }
        }));
      };

      // ── Kebab menu (3-line row actions) — single-portal design ──
      // The menu is rendered ONCE per render-pass via React Portal into
      // document.body. Coords come from the originating click (clientX/Y)
      // so behaviour is stable regardless of transformed/scrolled ancestors
      // creating a containing-block for the otherwise-fixed positioning.
      const MENU_W = 210;
      const MENU_H_EST = 300;
      const openMenuAt = (user, x, y) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let top  = y + 4;
        let left = x;
        if (top + MENU_H_EST > vh - 8) top = Math.max(8, y - MENU_H_EST - 4);
        if (left < 8) left = 8;
        if (left + MENU_W > vw - 8) left = vw - MENU_W - 8;
        setOpenRowMenuId(user.id);
        setOpenRowMenuPos({ top, left });
      };
      const renderKebabButton = (user) => (
        <button
          type="button"
          className="ap-row-kebab"
          title="Row actions"
          onClick={(e) => { e.stopPropagation(); openMenuAt(user, e.clientX, e.clientY); }}
          style={{
            width: 28, height: 28, borderRadius: 6,
            border: '1px solid transparent',
            background: openRowMenuId === user.id ? '#EEF2FF' : 'transparent',
            color: '#475569',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="4" y1="7"  x2="20" y2="7"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="17" x2="20" y2="17"/>
          </svg>
        </button>
      );
      const openMenuUser = openRowMenuId ? usersById[openRowMenuId] : null;
      const renderMenuPortal = () => {
        if (!openMenuUser || !openRowMenuPos) return null;
        const u = openMenuUser;
        return createPortal(
          <div
            className="ap-row-menu"
            style={{
              position: 'fixed',
              top:  openRowMenuPos.top,
              left: openRowMenuPos.left,
              width: MENU_W,
              background: '#fff',
              border: '1px solid #E5E7EB', borderRadius: 10,
              boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
              zIndex: 100000, overflow: 'hidden',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Header: username (non-clickable label). */}
            <div className="ap-row-menu-header" title={u.fullname || u.username}>
              <span className="ap-row-menu-header__dot" style={{ background: STATUS_ACCENT[getUserStatusType(u)] || '#60A5FA' }} />
              <span className="ap-row-menu-header__name">{u.username}</span>
            </div>
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); handleShowUserInfo(u); }}>
              ℹ️ User info
            </button>
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); handleEditUser(u); }}>
              ✏️ Edit user
            </button>
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); navUserFn(u, 'brands'); }}>
              🏠 View brands ({u.brand_count ?? 0})
            </button>
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); navUserFn(u, 'branches'); }}>
              📍 View branches ({u.branch_count ?? 0})
            </button>
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); navUserFn(u, 'devices'); }}>
              📺 View devices
            </button>
            <div style={{ height: 1, background: '#F1F5F9', margin: '2px 0' }} />
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); handleToggleSuspend(u); }}
              style={{ color: u.is_suspended ? '#15803D' : '#B45309' }}>
              {u.is_suspended ? '✅ Unsuspend' : '⛔ Suspend'}
            </button>
            <button type="button" className="ap-row-menu-item"
              onClick={() => { closeRowMenu(); handleDeleteUser(u); }}
              style={{ color: '#B91C1C' }}>
              🗑 Delete user
            </button>
          </div>,
          document.body
        );
      };

      const renderUserCard = (user) => {
        const { statusType, accent, roleType, proDaysLeft, initials, brandCount, branchCount, deviceCount } = decorate(user);
        const navUser = (kind) => navUserFn(user, kind);
        return (
                  <div key={user.id} className="user-card" style={{
                    background: '#fff',
                    borderTop:    '1.5px solid #EBEBEB',
                    borderRight:  '1.5px solid #EBEBEB',
                    borderBottom: '1.5px solid #EBEBEB',
                    borderLeft:   `5px solid ${accent}`,
                    borderRadius: 14,
                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0,
                  }}>
                    {/* keep the original status-flag node for any tests/CSS that target it */}
                    <div className={`status-flag ${statusType}`} style={{ display: 'none' }}></div>

                    {/* ── Header: avatar + name/phone/fullname/activity + role badge ── */}
                    <div style={{ padding: '11px 14px 10px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div
                        onClick={(e) => { e.stopPropagation(); handleShowUserInfo(user); }}
                        title="Show user info"
                        style={{
                          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                          background: user.brand_logo ? '#f5f5f5' : accent,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: 1,
                          boxShadow: `0 3px 10px ${accent}55`,
                          cursor: 'pointer', overflow: 'hidden',
                        }}>
                        {user.brand_logo
                          ? <img src={user.brand_logo} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          : initials}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#111',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.username}
                        </div>

                        {user.phone ? (
                          <a
                            className="phone-button"
                            href={`tel:${user.phone}`}
                            title={`Call ${user.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              marginTop: 4,
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 12, fontWeight: 500,
                              color: accent, textDecoration: 'none',
                              transition: 'font-weight 0.1s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.fontWeight = '700'; }}
                            onMouseLeave={e => { e.currentTarget.style.fontWeight = '500'; }}
                          >
                            📞 {user.phone}
                          </a>
                        ) : (
                          <div style={{ marginTop: 4, color: '#ccc', fontSize: 12 }}>📞 —</div>
                        )}

                        <div style={{ marginTop: 4, color: '#555', fontSize: 12,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.fullname || <span style={{ color: '#bbb' }}>No full name</span>}
                        </div>

                        <div className={`last-activity ${statusType}`} style={{ marginTop: 3, fontSize: 11 }}>
                          {formatActivityText(user, statusType)}
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                        <div style={{
                          background: roleType.bg, color: roleType.color,
                          border: `1px solid ${roleType.border}`,
                          borderRadius: 20, padding: '2px 10px', fontSize: 10, fontWeight: 800,
                          letterSpacing: 0.6,
                        }}>{roleType.label}</div>
                        {proDaysLeft !== null && (
                          <div style={{
                            fontSize: 10, fontWeight: 700,
                            color:      proDaysLeft <= 10 ? '#DC2626' : '#D97706',
                            background: proDaysLeft <= 10 ? '#FEF2F2' : '#FFFBEB',
                            border:     `1px solid ${proDaysLeft <= 10 ? '#FCA5A5' : '#FDE68A'}`,
                            borderRadius: 20, padding: '1px 7px', whiteSpace: 'nowrap',
                            animation: 'ap-pulse 1.4s ease-in-out infinite',
                          }}>
                            {proDaysLeft <= 0 ? '⛔ Expired' : proDaysLeft <= 10 ? `⚠️ ${proDaysLeft}d left` : `${proDaysLeft}d left`}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Stats row — each cell navigates independently ── */}
                    <div style={{
                      display: 'flex',
                      borderTop:    '1px solid #F5F5F5',
                      borderBottom: '1px solid #F5F5F5',
                      padding: 0,
                    }}>
                      {/* Brands */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navUser('brands'); }}
                        title={`Show ${user.username}'s brands`}
                        style={{
                          flex: 1, padding: '7px 6px', borderRight: '1px solid #F5F5F5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          background: 'none', border: 'none', cursor: 'pointer',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#F0F7FF'}
                        onMouseOut={e => e.currentTarget.style.background = 'none'}
                      >
                        <span style={{ color: '#2563EB', fontSize: 14 }}>🏠</span>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{brandCount}</span>
                        <span style={{ fontSize: 9, color: '#888', fontWeight: 700, letterSpacing: 0.5 }}>BRANDS</span>
                      </button>
                      {/* Branches */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navUser('branches'); }}
                        title={`Show ${user.username}'s branches`}
                        style={{
                          flex: 1, padding: '7px 6px', borderRight: '1px solid #F5F5F5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          background: 'none', border: 'none', cursor: 'pointer',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#F0FDF4'}
                        onMouseOut={e => e.currentTarget.style.background = 'none'}
                      >
                        <span style={{ color: '#16A34A', fontSize: 14 }}>📍</span>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{branchCount}</span>
                        <span style={{ fontSize: 9, color: '#888', fontWeight: 700, letterSpacing: 0.5 }}>BRANCHES</span>
                      </button>
                      {/* Devices */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navUser('devices'); }}
                        title={`Show ${user.username}'s devices`}
                        style={{
                          flex: 1, padding: '7px 6px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          background: 'none', border: 'none', cursor: 'pointer',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#F5F3FF'}
                        onMouseOut={e => e.currentTarget.style.background = 'none'}
                      >
                        <span style={{ color: '#7C3AED', fontSize: 14 }}>📺</span>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{deviceCount}</span>
                        <span style={{ fontSize: 9, color: '#888', fontWeight: 700, letterSpacing: 0.5 }}>DEVICES</span>
                      </button>
                    </div>

                    {/* ── Action buttons ── */}
                    <div style={{ display: 'flex', gap: 8, padding: '8px 10px' }}>
                      <button
                        onClick={() => handleEditUser(user)}
                        style={{
                          flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #BFDBFE',
                          background: '#EFF6FF', color: '#1D4ED8', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          fontSize: 13, fontWeight: 600,
                        }}
                      >✏️ Edit</button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        style={{
                          flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #FECACA',
                          background: '#FEF2F2', color: '#B91C1C', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          fontSize: 13, fontWeight: 600,
                        }}
                      >🗑 Delete</button>
                    </div>
                  </div>
                );
              };
              // ── Table row renderer ─────────────────────────────────
              const renderTableRow = (user) => {
                const { statusType, accent, roleType, roleText, brandCount, branchCount, deviceCount, logoUrl, usernameInitial } = decorate(user);
                const suspended = !!user.is_suspended;
                return (
                  <tr key={user.id}
                    className={`ap-row ${suspended ? 'ap-row--suspended' : ''}`}
                    title="Click for menu · double-click to edit"
                    onClick={(e) => {
                      // Inner interactive elements stop propagation themselves
                      // (avatar / count / phone / kebab), so this only fires
                      // on inert row areas. Skip e.detail===2 — that's the
                      // second click of a dblclick which onDoubleClick handles.
                      if (e.detail >= 2) return;
                      openMenuAt(user, e.clientX, e.clientY);
                    }}
                    onDoubleClick={() => {
                      closeRowMenu();
                      handleEditUser(user);
                    }}
                  >
                    {/* User cell — avatar + username (fullname moved to its own column) */}
                    <td className="ap-td ap-td--user">
                      <div className="ap-user-cell">
                        <div className="ap-avatar"
                          style={{ background: logoUrl ? '#f5f5f5' : accent }}
                          onClick={(e) => { e.stopPropagation(); handleShowUserInfo(user); }}
                          title="Show user info"
                        >
                          {logoUrl
                            ? <img src={logoUrl} alt={user.username} />
                            : <span style={{ fontSize: 13 }}>{usernameInitial}</span>}
                        </div>
                        <div className="ap-user-name">
                          <div className="ap-user-line1">
                            <span className="ap-username">{user.username}</span>
                            {suspended && <span className="ap-pill ap-pill--suspended">SUSPENDED</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Role — plain-language text instead of a coloured pill */}
                    <td className="ap-td">
                      <span className="ap-role-badge" style={{
                        background: roleType.bg, color: roleType.color, border: `1px solid ${roleType.border}`,
                      }}>{roleText}</span>
                    </td>
                    {/* Status + last activity. On mobile the text is hidden and
                        only the coloured dot remains (per design ask). */}
                    <td className="ap-td ap-td--status">
                      <div className="ap-status-cell">
                        <span className="ap-status-dot" style={{ background: accent }} title={formatActivityText(user, statusType)} />
                        <span className={`ap-status-text ap-status-${statusType}`}>
                          {formatActivityText(user, statusType)}
                        </span>
                      </div>
                    </td>
                    {/* Counts: brands · branches · devices — clickable */}
                    <td className="ap-td ap-td--counts">
                      <button type="button" className="ap-count" title={`${user.username}'s brands`}
                        onClick={(e) => { e.stopPropagation(); navUserFn(user, 'brands'); }}>
                        <span className="ap-count-icon" style={{ color: '#2563EB' }}>🏠</span>
                        <span className="ap-count-num">{brandCount}</span>
                      </button>
                      <button type="button" className="ap-count" title={`${user.username}'s branches`}
                        onClick={(e) => { e.stopPropagation(); navUserFn(user, 'branches'); }}>
                        <span className="ap-count-icon" style={{ color: '#16A34A' }}>📍</span>
                        <span className="ap-count-num">{branchCount}</span>
                      </button>
                      <button type="button" className="ap-count" title={`${user.username}'s devices`}
                        onClick={(e) => { e.stopPropagation(); navUserFn(user, 'devices'); }}>
                        <span className="ap-count-icon" style={{ color: '#7C3AED' }}>📺</span>
                        <span className="ap-count-num">{deviceCount}</span>
                      </button>
                    </td>
                    {/* Name (fullname) — separate column right before Phone. */}
                    <td className="ap-td ap-td--fullname">
                      {user.fullname
                        ? <span style={{ fontSize: 12.5, color: '#374151' }}>{user.fullname}</span>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    {/* Phone */}
                    <td className="ap-td ap-td--phone">
                      {user.phone
                        ? <a href={`tel:${user.phone}`} className="ap-phone" title={`Call ${user.phone}`} onClick={e => e.stopPropagation()}>📞 {user.phone}</a>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    {/* Kebab cell removed — row click opens the menu directly. */}
                  </tr>
                );
              };

              // ── Render the matching view ───────────────────────────
              if (sortedFlat.length === 0) {
                return (
                  <div style={{ padding: '40px 0', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>
                    No users match the current filters.
                  </div>
                );
              }

              if (userViewMode === 'table') {
                return (
                  <>
                    <div className="ap-table-wrap">
                      <table className="ap-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>Status · Last activity</th>
                            <th>Brands · Branches · Devices</th>
                            <th>Name</th>
                            <th>Phone</th>
                          </tr>
                        </thead>
                        <tbody>{sortedFlat.map(renderTableRow)}</tbody>
                      </table>
                    </div>
                    {renderMenuPortal()}
                  </>
                );
              }

              // Cards view — role-grouped sections. Only two roles exist since
              // the staff feature was removed 2026-10-27; the brand_manager /
              // branch_manager / operator buckets went with it (they were
              // already empty: 157 owners + 2 admins, no staff rows at all).
              const cardGroups = [
                { key: 'admin',          title: '⚙️ Admins' },
                { key: 'owner',          title: '👤 Owners' },
              ];
              return (
                <>
                  {cardGroups.map(g => {
                    const inGroup = sortedFlat.filter(u => roleOf(u) === g.key);
                    if (inGroup.length === 0) return null;
                    return (
                      <React.Fragment key={g.key}>
                        <div style={sectionTitleStyle}>
                          {g.title}
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', background: '#F3F4F6', borderRadius: 12, padding: '1px 8px' }}>{inGroup.length}</span>
                        </div>
                        <div className="users-grid">{inGroup.map(renderUserCard)}</div>
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'storage' && (
          <div className="storage-content">
            <DavActivity initialTab={storageInitialTab} />
          </div>
        )}

        {activeTab === 'backup' && (
          <div className="backup-content">
            <TimelineBackup />
          </div>
        )}
      </div>

      {/* Actions Menu Dropdown */}
      {showUserActionsMenu && (
        <div className="actions-menu-dropdown" ref={actionsMenuRef}>
          <div className={`device-name-header ${users.find(u => u.username === showUserActionsMenu)?.is_currently_online ? 'online' : 'offline'}`}>
            {showUserActionsMenu}
          </div>
          <div className="actions-menu-item edit" onClick={() => handleEditUser(users.find(u => u.username === showUserActionsMenu))}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
            Edit User
          </div>
          <div className="actions-menu-item info" onClick={() => handleShowUserInfo(users.find(u => u.username === showUserActionsMenu))}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            User Info
          </div>
          <div className="actions-menu-item delete" onClick={() => handleDeleteUser(users.find(u => u.username === showUserActionsMenu))}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            Delete User
          </div>
        </div>
      )}

      {/* Phone Menu Dropdown */}
      {showPhoneMenu && (
        <div className="phone-menu-dropdown-global">
          <div className="actions-menu-item" onClick={() => handlePhoneAction(users.find(u => u.username === showPhoneMenu)?.phone, 'call')}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            Call
          </div>
          <div className="actions-menu-item" onClick={() => handlePhoneAction(users.find(u => u.username === showPhoneMenu)?.phone, 'whatsapp')}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
            </svg>
            WhatsApp
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (() => {
        // Close handler: if we're editing a staff user with a parent owner,
        // jump back to that owner's edit modal instead of closing.
        const closeOrReturn = () => {
          const parentId = editMode && editedUser?.parent_user_id;
          const parent = parentId ? users.find(u => u.id === parentId) : null;
          setShowAddUserModal(false);
          if (parent) setTimeout(() => handleEditUser(parent), 60);
        };
        return (
        <div className="user-modal-overlay">
          <div className="user-modal">
            <button className="close-button" onClick={closeOrReturn}>
              ×
            </button>
            {/* "← back to {owner}" link — appears when editing a staff member
                whose parent_user_id is set, so the admin can jump back to that
                owner's edit modal in one click. */}
            {editMode && editedUser.parent_user_id && (() => {
              const parentRow = users.find(u => u.id === editedUser.parent_user_id);
              if (!parentRow) return null;
              return (
                <button
                  type="button"
                  onClick={() => handleEditUser(parentRow)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#1D4ED8', fontSize: 12, fontWeight: 600,
                    padding: '4px 0', marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  ← back to owner ({parentRow.username})
                </button>
              );
            })()}
            <h2>{editMode ? 'Edit User' : 'Add New User'}</h2>
            <form onSubmit={editMode ? handleUpdateUser : handleCreateUser}>
              <div className="form-row">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={editMode ? editedUser.username : newUser.username}
                    onChange={(e) => editMode 
                      ? setEditedUser({...editedUser, username: e.target.value})
                      : setNewUser({...newUser, username: e.target.value})
                    }
                    required
                    disabled={editMode}
                    className={editMode ? 'disabled-input' : ''}
                  />
                </div>
                <div className="form-group">
                  <label>{editMode ? 'Leave blank to keep current' : 'Password'}</label>
                  <input
                    type="password"
                    value={editMode ? editedUser.new_password : newUser.password}
                    onChange={(e) => editMode 
                      ? setEditedUser({...editedUser, new_password: e.target.value})
                      : setNewUser({...newUser, password: e.target.value})
                    }
                    required={!editMode}
                  />
                </div>
              </div>
              
              {/* Row 2: Email · Full Name · Phone — equal-width cells, always visible */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 14 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={editMode ? editedUser.email : newUser.email}
                    onChange={(e) => editMode
                      ? setEditedUser({...editedUser, email: e.target.value})
                      : setNewUser({...newUser, email: e.target.value})
                    }
                    style={{ height: 38 }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={editMode ? editedUser.fullname : newUser.fullname}
                    onChange={(e) => editMode
                      ? setEditedUser({...editedUser, fullname: e.target.value})
                      : setNewUser({...newUser, fullname: e.target.value})
                    }
                    style={{ height: 38 }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                  <label>Phone</label>
                  <input
                    type="text"
                    value={editMode ? editedUser.phone : newUser.phone}
                    onChange={(e) => editMode
                      ? setEditedUser({...editedUser, phone: e.target.value})
                      : setNewUser({...newUser, phone: e.target.value})
                    }
                    style={{ height: 38 }}
                  />
                </div>
              </div>

              {/* ── Account Type (Owner / Administrator) ──
                  Replaces the legacy User Type dropdown for role selection. The
                  buttons drive `role`/`is_admin`/`user_type` together so legacy
                  checks keep working. Staff was the third option until
                  2026-10-27. */}
              {(() => {
                const cur     = editMode ? editedUser : newUser;
                const setCur  = editMode ? setEditedUser : setNewUser;
                const role    = cur.role || (cur.is_admin ? 'admin' : 'owner');
                // Admin or Owner — nothing else exists since the staff feature
                // was removed 2026-10-27. Any legacy brand_manager /
                // branch_manager / operator row would read as an owner here;
                // there are none in the DB (verified: 157 owner + 2 admin).
                const accountType = role === 'admin' ? 'admin' : 'owner';

                const setAccountType = (kind) => {
                  if (kind === 'owner') {
                    setCur(prev => ({ ...prev, role: 'owner', is_admin: false,
                      user_type: prev.user_type === 'admin' ? 'regular' : (prev.user_type || 'regular'),
                      parent_user_id: '' }));
                  } else if (kind === 'admin') {
                    setCur(prev => ({ ...prev, role: 'admin', is_admin: true, user_type: 'admin', parent_user_id: '' }));
                  }
                };

                // Hide the Account Type segmented control on ANY edit — the
                // user's high-level role (admin / owner) doesn't change through
                // this modal.
                const hideAccountTypeSwitcher = editMode;

                return (
                  <div style={{
                    padding: 12, borderRadius: 10, background: '#FAFAFA',
                    border: '1.5px solid #E5E7EB', marginBottom: 14,
                  }}>
                    {!hideAccountTypeSwitcher && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8, letterSpacing: 0.5 }}>
                      ACCOUNT TYPE
                    </div>
                    )}
                    {/* Segmented control: Administrator / Owner / Staff */}
                    {!hideAccountTypeSwitcher && (
                    <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderRadius: 8, overflow: 'hidden', border: '1.5px solid #D1D5DB' }}>
                      {[
                        { id: 'admin', label: '⚙️ Administrator', hint: 'Full system access — no parent, no brands' },
                        { id: 'owner', label: '👤 Owner',         hint: 'Owns brands' },
                      ].map(t => {
                        const active = accountType === t.id;
                        return (
                          <button
                            key={t.id} type="button" title={t.hint}
                            onClick={() => setAccountType(t.id)}
                            style={{
                              flex: 1, padding: '8px 10px', border: 'none',
                              background: active ? '#1D4ED8' : '#fff',
                              color: active ? '#fff' : '#374151',
                              fontWeight: 700, fontSize: 13, cursor: 'pointer',
                              borderRight: '1px solid #D1D5DB',
                            }}
                          >{t.label}</button>
                        );
                      })}
                    </div>
                    )}

                    {accountType === 'owner' && (() => {
                      // Hierarchical permissions grid (2026-05-17 Phase C).
                      // Scopes (TV / Menu / POS / Kitchen) with nested sub-caps.
                      // Locked decisions:
                      //   1. Toggling scope ON auto-enables all its sub-caps.
                      //   2. No expiry UI (owner, 2026-10-27). Scope- and cap-level
                      //      `expires_at` still exist in `access` and the backend
                      //      still enforces them -- there is simply no longer a way
                      //      to SET one from this form. Verified lossless before
                      //      removing: 0/159 users had any expires_at value set.
                      //   3. Add Staff was a per-owner toggle, then (2026-09-07)
                      //      an always-on part of being an owner. The entire
                      //      staff feature was removed 2026-10-27 (owner).
                      const access = (cur.access && typeof cur.access === 'object' && Object.keys(cur.access).length)
                        ? cur.access
                        : emptyAccess();

                      const setAccess = (next) => setCur({ ...cur, access: next });
                      const clone = (x) => JSON.parse(JSON.stringify(x));

                      const toggleScope = (scopeKey, on) => {
                        const cat = SCOPES[scopeKey];
                        const next = clone(access);
                        if (!next[scopeKey]) next[scopeKey] = { enabled: false, expires_at: null, caps: {} };
                        next[scopeKey].enabled = on;
                        for (const capKey of Object.keys(cat.caps || {})) {
                          if (!next[scopeKey].caps[capKey]) {
                            next[scopeKey].caps[capKey] = { enabled: false, expires_at: null };
                          }
                          next[scopeKey].caps[capKey].enabled = on;
                        }
                        setAccess(next);
                      };

                      const toggleCap = (scopeKey, capKey, on) => {
                        const next = clone(access);
                        if (!next[scopeKey]) return;
                        if (!next[scopeKey].caps[capKey]) {
                          next[scopeKey].caps[capKey] = { enabled: false, expires_at: null };
                        }
                        next[scopeKey].caps[capKey].enabled = on;
                        setAccess(next);
                      };

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {/* Scope cards — one per top-level scope */}
                          {scopesByOrder().map(([scopeKey, scopeDef]) => {
                            const scope = access[scopeKey] || { enabled: false, expires_at: null, caps: {} };
                            const enabled = scope.enabled === true;
                            const isComing = !!scopeDef.comingSoon;
                            const capList = capsByOrder(scopeKey);

                            return (
                              <div key={scopeKey} style={{
                                border: `1.5px solid ${enabled ? '#1D4ED8' : '#E5E7EB'}`,
                                background: enabled ? '#F0F7FF' : '#FAFAFA',
                                borderRadius: 10, padding: 10,
                              }}>
                                {/* Header row: icon + label + master toggle */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, fontSize: 14, fontWeight: 700, color: enabled ? '#1D4ED8' : '#374151' }}>
                                    <input type="checkbox" checked={enabled}
                                      onChange={e => toggleScope(scopeKey, e.target.checked)} />
                                    <span style={{ fontSize: 16 }}>{scopeDef.icon}</span>
                                    {scopeDef.label}
                                  </label>
                                  {isComing && (
                                    <span style={{
                                      fontSize: 10, fontWeight: 700, color: '#92400E',
                                      background: '#FEF3C7', border: '1px solid #FCD34D',
                                      borderRadius: 20, padding: '2px 8px',
                                    }}>coming soon</span>
                                  )}
                                </div>

                                {/* Sub-caps (only when scope enabled and not coming-soon) */}
                                {enabled && !isComing && capList.length > 0 && (
                                  <div style={{
                                    marginTop: 8, paddingTop: 8,
                                    borderTop: '1px dashed #BFDBFE',
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${capList.length}, 1fr)`,
                                    gap: 6,
                                  }}>
                                    {capList.map(([capKey, capDef]) => {
                                      const cap = scope.caps?.[capKey] || { enabled: false, expires_at: null };
                                      const capOn = cap.enabled === true;
                                      return (
                                        <div key={capKey} style={{
                                          display: 'flex', alignItems: 'center', gap: 6,
                                          padding: '4px 6px', borderRadius: 6,
                                          background: capOn ? '#fff' : 'transparent',
                                          border: capOn ? '1px solid #BFDBFE' : '1px solid transparent',
                                        }}>
                                          <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: capOn ? '#1E40AF' : '#6B7280', flex: 1 }}>
                                            <input type="checkbox" checked={capOn}
                                              onChange={e => toggleCap(scopeKey, capKey, e.target.checked)} />
                                            {capDef.label}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Coming-soon placeholder for POS/Kitchen */}
                                {enabled && isComing && (
                                  <div style={{
                                    marginTop: 8, paddingTop: 8,
                                    borderTop: '1px dashed #FCD34D',
                                    fontSize: 11, color: '#92400E',
                                  }}>
                                    Scope unlocked. Sub-capabilities will appear when this system ships.
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Display Manager (renamed from "Screen Manager" to match
                              the sidebar's own group name) — hand-built, not
                              SCOPES-driven like the card above. Its 3 items
                              deliberately live in different places inside `access`
                              so none of this needed a data migration:
                                F&B Menu     → access.menu.enabled (the scope-level
                                  flag the old 4-sub-cap "Menu Manager" card used to
                                  carry; those sub-caps were never read anywhere so
                                  they're just dropped from this UI, not migrated)
                                Info Screen  → access.tv.caps.info_screen (toggleCap,
                                  same storage key as before — just shown in a
                                  different card now)
                                Live Stream  → access.live_stream.enabled (new scope,
                                  2026-10-27 — owner: "add the checkbox, I'll tell you
                                  what it does later")
                              Order/labels match the Sidebar.js nav group exactly.
                              Master checkbox (owner, 2026-10-27) mirrors TV Manager's:
                              it is not a stored flag of its own — there is no
                              `display_manager` scope — it just reads "all three on"
                              and writes all three at once. Indeterminate while only
                              some are on. The three stay off by default (opt-in
                              extras, not the baseline tier). */}
                          {(() => {
                            const infoScreenOn = access.tv?.caps?.info_screen?.enabled === true;
                            const fnbOn        = access.menu?.enabled === true;
                            const liveOn       = access.live_stream?.enabled === true;
                            const setScopeOn = (scopeKey, on) => {
                              const next = clone(access);
                              if (!next[scopeKey]) next[scopeKey] = { enabled: false, expires_at: null, caps: {} };
                              next[scopeKey].enabled = on;
                              setAccess(next);
                            };
                            // Info Screen is stored as a CAP under the tv scope, and
                            // canAccessCap() refuses any cap whose parent scope is
                            // off. Granting it while TV Manager is unticked would
                            // show as granted and silently not work -- so it lights
                            // the baseline TV scope up with it.
                            const setInfoScreenOn = (on) => {
                              const next = clone(access);
                              if (!next.tv) next.tv = { enabled: false, expires_at: null, caps: {} };
                              if (!next.tv.caps) next.tv.caps = {};
                              if (!next.tv.caps.info_screen) next.tv.caps.info_screen = { enabled: false, expires_at: null };
                              next.tv.caps.info_screen.enabled = on;
                              if (on) next.tv.enabled = true;
                              setAccess(next);
                            };
                            const allOn  = infoScreenOn && fnbOn && liveOn;
                            const someOn = (infoScreenOn || fnbOn || liveOn) && !allOn;
                            // One write, not three chained setAccess calls -- each
                            // would clone the PREVIOUS access and the last would win,
                            // silently dropping the other two toggles.
                            const setAllDisplay = (on) => {
                              const next = clone(access);
                              for (const k of ['menu', 'live_stream']) {
                                if (!next[k]) next[k] = { enabled: false, expires_at: null, caps: {} };
                                next[k].enabled = on;
                              }
                              if (!next.tv) next.tv = { enabled: false, expires_at: null, caps: {} };
                              if (!next.tv.caps) next.tv.caps = {};
                              if (!next.tv.caps.info_screen) next.tv.caps.info_screen = { enabled: false, expires_at: null };
                              next.tv.caps.info_screen.enabled = on;
                              if (on) next.tv.enabled = true;   // see setInfoScreenOn
                              setAccess(next);
                            };
                            const row = (label, on, onChange) => (
                              <div key={label} style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '4px 6px', borderRadius: 6,
                                background: on ? '#fff' : 'transparent',
                                border: on ? '1px solid #BFDBFE' : '1px solid transparent',
                              }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: on ? '#1E40AF' : '#6B7280', flex: 1 }}>
                                  <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} />
                                  {label}
                                </label>
                              </div>
                            );
                            return (
                              <div style={{
                                border: '1.5px solid #E5E7EB', background: '#FAFAFA',
                                borderRadius: 10, padding: 10,
                              }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: allOn ? '#1D4ED8' : '#374151', marginBottom: 8, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={allOn}
                                    ref={el => { if (el) el.indeterminate = someOn; }}
                                    onChange={e => setAllDisplay(e.target.checked)} />
                                  <span style={{ fontSize: 16 }}>📁</span>
                                  Display Manager
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                                  {row('F&B Menu',    fnbOn,        on => setScopeOn('menu', on))}
                                  {row('Info Screen', infoScreenOn, on => setInfoScreenOn(on))}
                                  {row('Live Stream', liveOn,       on => setScopeOn('live_stream', on))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}


                  </div>
                );
              })()}




              {/* 2026-10-27 — Device Permissions section removed per owner request:
                  device↔owner assignment now happens exclusively from the player
                  card and the Adopt flow (handleAdoptDevice in App.js); having a
                  second place to set the same thing here was redundant and the
                  two could silently disagree. See also: removal of
                  updateUserDevicePermissions()/handleToggleDevicePermission()
                  below, which only existed to serve this section. */}

              <div className="form-buttons">
                <button type="button" onClick={closeOrReturn}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {editMode ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })()}

      {/* User Info Modal */}
      {showUserInfoModal && selectedUserForInfo && (
        <div className="user-modal-overlay">
          <div className="user-modal user-info-modal">
            <button className="close-button" onClick={() => setShowUserInfoModal(false)}>
              ×
            </button>
            <h2>User Information</h2>
            <div className="user-info-grid">
              <div className="info-item">
                <div className="info-label">User:</div>
                <div className="info-value">{selectedUserForInfo.username}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Full Name:</div>
                <div className="info-value">{selectedUserForInfo.fullname || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Email:</div>
                <div className="info-value">{selectedUserForInfo.email || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Phone:</div>
                <div className="info-value">
                  {selectedUserForInfo.phone ? (
                    <a href={`tel:${selectedUserForInfo.phone}`} className="phone-link">
                      {selectedUserForInfo.phone}
                    </a>
                  ) : (
                    'Not provided'
                  )}
                </div>
              </div>
              
              {/* --- NEW: LOGIN IP ADDRESS --- */}
              <div className="info-item">
                <div className="info-label">Login IP:</div>
                <div className="info-value">{selectedUserForInfo.last_login_ip || 'N/A'}</div>
              </div>
              
              {/* --- NEW: LOGIN LOCATION --- */}
              <div className="info-item">
                <div className="info-label">Location:</div>
                <div className="info-value">{selectedUserForInfo.last_login_location || 'N/A'}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Status:</div>
                <div className={`info-value ${selectedUserForInfo.is_currently_online ? 'online' : 'offline'}`}>
                  {selectedUserForInfo.is_currently_online ? 'Online' : 'Offline'}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">State:</div>
                <div className="info-value" style={{ 
                  color: selectedUserForInfo.user_type === 'admin' || selectedUserForInfo.user_type === 'pro' ? '#f44336' : 'inherit',
                  fontWeight: selectedUserForInfo.user_type === 'admin' || selectedUserForInfo.user_type === 'pro' ? 'bold' : 'normal',
                  textTransform: 'capitalize'
                }}>
                  {selectedUserForInfo.user_type || 'regular'}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Players:</div>
                <div className="info-value">{userPermissions[selectedUserForInfo.username] || 0}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Last Activity:</div>
                <div className="info-value">{formatLastActivity(selectedUserForInfo.last_login)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll-to-top now lives in Sidebar.js (global) — no per-page copy needed. */}
    </div>
  );
});

export default AdminPanel;